import { cn } from "@/utils";
import { ToolCall } from "@cohesive/api-client";
import { Ban, Check, ChevronRight, Loader2, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { ToolCallIcon } from "../ToolCallIcon";
import { CommandBlock } from "./CommandBlock";

type Tool_E2bBashProps = {
  call: ToolCall;
};

/**
 * Compact transcript row for a sandbox command that's no longer pending — it
 * either ran, is running, or was denied. Leads with the plain-language action
 * (what the command was for) and a single status chip that highlights the
 * approval/denial outcome, with the raw command (and output, if it ran) tucked
 * behind a click. Replaces the heavyweight confirmation card for every
 * non-pending state.
 */
export function Tool_E2bBash({ call }: Tool_E2bBashProps) {
  const args = call.function.arguments as Record<string, any>;
  const command = args?.command as string | undefined;
  const actionDescription = args?.action_description as string | undefined;
  const result = (call.function.result?.data ?? call.result?.data) as
    | { content?: string }
    | undefined;
  const error = call.function.result?.error ?? call.result?.error;

  const [expanded, setExpanded] = useState(false);

  if (!command) return null;

  const state = resolveBashState({
    status: call.user_confirmation_status,
    hasError: !!error?.message,
  });
  const denied = state.kind === "denied";

  const raw = actionDescription?.trim();
  const title = raw
    ? raw.charAt(0).toUpperCase() + raw.slice(1)
    : "Terminal command";

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="group flex w-full items-center gap-2 px-4 py-2 text-left outline-none"
      >
        {/* Terminal mark, aligned to and masking the activity rail at x≈28px. */}
        <span className="relative z-[1] flex size-6 shrink-0 items-center justify-center bg-background">
          <ToolCallIcon toolCall={call} />
        </span>

        <span
          className={cn(
            "min-w-0 flex-1 truncate font-sans text-sm",
            // Denied commands never ran — dim them so they read as "didn't happen".
            denied ? "text-n-50" : "text-n-70",
          )}
        >
          {title}
        </span>

        <span
          className={cn(
            "inline-flex shrink-0 items-center gap-1 text-xs font-medium",
            state.tone,
          )}
        >
          <state.Icon
            className={cn(
              "size-3.5",
              state.kind === "running" && "animate-spin",
            )}
          />
          {state.label}
        </span>

        <ChevronRight
          className={cn(
            "size-3.5 shrink-0 text-n-40 transition-transform duration-150 ease-out group-hover:text-n-60",
            expanded && "rotate-90",
          )}
        />
      </button>

      {expanded && (
        <div className="mx-4 mb-3 overflow-hidden rounded-lg border">
          <CommandBlock command={command} />
          {(result?.content || error?.message) && (
            <OutputBlock output={result?.content} error={error?.message} />
          )}
        </div>
      )}
    </div>
  );
}

type BashState = {
  kind: "ran" | "failed" | "denied" | "running";
  label: string;
  tone: string;
  Icon: typeof Check;
};

/**
 * Maps confirmation status + execution result to the single status chip. Denial
 * is the explicit user action (rejected); everything else either ran or is
 * running. "Ran"/"Failed" both imply the command was approved (or didn't need
 * approval) and executed — the outcome is what's worth surfacing.
 */
function resolveBashState({
  status,
  hasError,
}: {
  status?: string;
  hasError: boolean;
}): BashState {
  if (status === "rejected") {
    return {
      kind: "denied",
      label: "Denied",
      tone: "text-muted-foreground",
      Icon: Ban,
    };
  }
  if (status === "confirmed-processing" || status === "confirmed") {
    // Running/completed states show only their icon — the spinner already reads
    // as "running" and the check as "done", so the label is redundant.
    return {
      kind: "running",
      label: "",
      tone: "text-muted-foreground",
      Icon: Loader2,
    };
  }
  if (hasError) {
    return {
      kind: "failed",
      label: "Failed",
      tone: "text-lc-blocked",
      Icon: TriangleAlert,
    };
  }
  return {
    kind: "ran",
    label: "",
    tone: "text-lc-active",
    Icon: Check,
  };
}

function OutputBlock({ output, error }: { output?: string; error?: string }) {
  const text = error || output || "";
  const isLong = text.split("\n").length > 12;
  const [expanded, setExpanded] = useState(!isLong);

  if (!text) return null;

  return (
    <div className="bg-card border-t border-neutral-200 dark:border-neutral-800">
      <div className="px-3 py-1.5 text-xs text-neutral-400 border-b border-neutral-100 dark:border-neutral-800">
        {error ? "Error" : "Output"}
      </div>
      <div className="relative">
        <pre
          className={cn(
            "px-3 py-2.5 text-sm font-mono whitespace-pre-wrap break-all leading-relaxed overflow-x-auto",
            error
              ? "text-lc-blocked dark:text-lc-blocked"
              : "text-neutral-700 dark:text-neutral-300",
            !expanded && "max-h-[200px] overflow-hidden",
          )}
        >
          {text}
        </pre>
        {isLong && !expanded && (
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-neutral-50 dark:from-neutral-900 to-transparent" />
        )}
      </div>
      {isLong && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="w-full py-1.5 text-xs text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors border-t border-neutral-100 dark:border-neutral-800"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
}
